// Shared draft types + helpers for the Manufacturing → Plants panel's
// dealer roster (right pane). Used by useManufacturingPlantDealersTab
// (the stateful hook) and the section components that render the
// create/edit forms.
//
// Previously lived inline in useLogisticsDealersPanel.ts; extracted on
// the Plants↔Dealers union slice so the new attach picker can share
// the same shapes without importing through the hook.
import type {
  MfgDealerAddress,
  MfgDealerLocation,
} from "@/lib/manufacturing/dealerTypes";

export type SubscriberEditDraft = {
  primary_plant_id: string;
  notes: string;
};

export type LocationDraft = {
  name: string;
  street: string;
  city: string;
  state: string;
  postal_code: string;
  primary_plant_id: string;
  linked_dealer_org_id: string;
  contact_name: string;
  contact_phone: string;
  contact_email: string;
  notes: string;
  is_active: boolean;
};

export const EMPTY_LOCATION_DRAFT: LocationDraft = {
  name: "",
  street: "",
  city: "",
  state: "",
  postal_code: "",
  primary_plant_id: "",
  linked_dealer_org_id: "",
  contact_name: "",
  contact_phone: "",
  contact_email: "",
  notes: "",
  is_active: true,
};

export function locationToDraft(loc: MfgDealerLocation): LocationDraft {
  const addr = (loc.address as MfgDealerAddress) ?? {};
  return {
    name: loc.name,
    street: typeof addr.street === "string" ? addr.street : "",
    city: typeof addr.city === "string" ? addr.city : "",
    state: typeof addr.state === "string" ? addr.state : "",
    postal_code: typeof addr.postal_code === "string" ? addr.postal_code : "",
    primary_plant_id: loc.primary_plant_id ?? "",
    linked_dealer_org_id: loc.linked_dealer_org_id ?? "",
    contact_name: loc.contact_name,
    contact_phone: loc.contact_phone,
    contact_email: loc.contact_email,
    notes: loc.notes,
    is_active: loc.is_active,
  };
}

export function buildAddressPayload(d: LocationDraft): Record<string, string> {
  const out: Record<string, string> = {};
  if (d.street.trim()) out.street = d.street.trim();
  if (d.city.trim()) out.city = d.city.trim();
  if (d.state.trim()) out.state = d.state.trim();
  if (d.postal_code.trim()) out.postal_code = d.postal_code.trim();
  return out;
}

// Sentinel value for the "Unattached" pseudo-row in the plants list.
// Selecting it scopes the right pane to dealers + locations whose
// primary_plant_id is null. Kept as a string literal (not a separate
// type) so it round-trips through useState<string | null> without a
// union widener.
export const UNATTACHED_PLANT_SENTINEL = "__unattached__" as const;
export type PlantSelection = string | typeof UNATTACHED_PLANT_SENTINEL | null;

export function isUnattachedSelection(selection: PlantSelection): boolean {
  return selection === UNATTACHED_PLANT_SENTINEL;
}

export function isRealPlantSelection(selection: PlantSelection): boolean {
  return typeof selection === "string" && selection !== UNATTACHED_PLANT_SENTINEL;
}
